import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangerMotDePasseInput,
  Connexion,
  ConnexionInput,
  EtatAuth,
  Utilisateur,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { fermerSession, ouvrirSession } from '@/lib/session';

export const cleEtatAuth = ['auth', 'etat'] as const;

/**
 * Mode d'authentification du serveur. Interrogé avant d'afficher quoi que ce
 * soit : sans lui, l'application ne sait pas si elle doit présenter un écran
 * de connexion ou laisser Cloudflare Access s'en charger.
 */
export function useEtatAuth() {
  return useQuery({
    queryKey: cleEtatAuth,
    queryFn: () => api.get<EtatAuth>('/auth/etat'),
    // La configuration ne bouge pas en cours de session ; seul `configure`
    // peut changer, et la page de connexion le redemande alors elle-même.
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useConnexion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnexionInput) => api.post<Connexion>('/auth/connexion', input),
    onSuccess: (connexion) => {
      ouvrirSession(connexion);
      // Les écrans précédents ont pu être remplis par un autre compte.
      client.clear();
    },
  });
}

export function useDeconnexion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ deconnecte: boolean }>('/auth/deconnexion'),
    /* La session locale tombe dans tous les cas : si l'appel échoue, le jeton
     * expirera de lui-même côté serveur, et rester connecté à l'écran alors
     * qu'on a demandé à sortir serait le pire des deux. */
    onSettled: () => {
      fermerSession();
      client.clear();
    },
  });
}

export function useMoi(actif: boolean) {
  return useQuery({
    queryKey: ['auth', 'moi'] as const,
    queryFn: () => api.get<Utilisateur>('/auth/moi'),
    enabled: actif,
    staleTime: 5 * 60_000,
  });
}

export function useChangerMotDePasse() {
  return useMutation({
    mutationFn: (input: ChangerMotDePasseInput) =>
      api.post<{ change: boolean; reconnexion_requise: boolean }>('/auth/mot-de-passe', input),
  });
}
